import { BACKEND_URL } from "@/lib/backendUrl";
import { useCallback, useEffect, useState } from "react";
import { Building2, Clock, Download, HeartHandshake, Loader2, ShieldCheck, Users,
  Wallet } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { money, shortDate } from "@/lib/format";

const PERIODS = [["30d", "Last 30 days"], ["90d", "Last 90 days"], ["365d", "Last 12 months"],
  ["ytd", "This year so far"]];

const Bar = ({ value, max, testId }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted" data-testid={testId}>
    <div className="h-full rounded-full bg-primary transition-[width] duration-500"
         style={{ width: `${max > 0 ? Math.min((value / max) * 100, 100) : 0}%` }} />
  </div>
);

export default function ImpactDashboard() {
  const [period, setPeriod] = useState("90d");
  const [data, setData] = useState(null);
  const [failure, setFailure] = useState("");

  const load = useCallback(async () => {
    setData(null);
    setFailure("");
    try {
      const res = await api.get(`/impact/overview?period=${period}`);
      setData(res.data);
    } catch (e) { setFailure(apiError(e)); toast.error(apiError(e)); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  if (failure) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Phase 8 · Cross-module reporting"
                    title="We could not draw the picture"
                    description="Nothing was changed — this is only a reading screen." />
        <Panel title="Could not load" testId="impact-failed">
          <p className="mb-3 text-[12.5px] text-muted-foreground" data-testid="impact-failed-message">
            {failure}
          </p>
          <Button size="sm" onClick={load} data-testid="impact-retry">Try again</Button>
        </Panel>
      </div>
    );
  }

  if (!data) return <Loading label="Pulling the whole platform together" />;

  const maxHours = Math.max(...data.programmes.map((p) => p.hours), 1);
  const kinds = Object.entries(data.hours.by_kind || {});

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Phase 8 · Cross-module reporting"
        title="One picture of the whole charity"
        description={`Hours, money, people and compliance drawn from the same records the modules write to — ${shortDate(data.window.from)} to ${shortDate(data.window.to)}.`}
        actions={(
          <div className="flex items-center gap-2">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[170px]" data-testid="impact-period">
                <SelectValue /></SelectTrigger>
              <SelectContent>
                {PERIODS.map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <a href={`${BACKEND_URL}/api/impact/export.csv?period=${period}`}
               data-testid="impact-export"
               className="inline-flex items-center rounded-full bg-primary px-4 py-2 text-[12px] text-primary-foreground transition-opacity duration-150 hover:opacity-90">
              <Download className="mr-1.5 h-3.5 w-3.5" /> Export CSV
            </a>
          </div>
        )}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi testId="impact-hours" label="Hours given" value={data.hours.total} icon={Clock}
             sub={`${data.hours.approved} approved · ${data.hours.awaiting_approval} awaiting`} />
        <Kpi testId="impact-income" label="Income" accent="teal" icon={Wallet}
             value={money(data.money.income, "INR", true)}
             sub={`${money(data.money.donations, "INR", true)} from donors`} />
        <Kpi testId="impact-people" label="People involved" icon={Users}
             value={data.people.staff + data.people.volunteers + data.people.apprentices}
             sub={`${data.people.volunteers} volunteers · ${data.people.apprentices} apprentices`} />
        <Kpi testId="impact-compliance" label="Evidence gaps" icon={ShieldCheck}
             accent={data.compliance.evidence_gaps ? "amber" : "emerald"}
             value={data.compliance.evidence_gaps}
             sub={`of ${data.compliance.rules_tracked} rules tracked`} />
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Panel title="Where the hours came from" testId="panel-impact-hours"
               kicker="Staff, volunteers and apprentices on one timesheet">
          {kinds.length === 0 ? <Empty label="No hours in this period" /> : (
            <div className="space-y-3" data-testid="impact-hours-breakdown">
              {kinds.sort((a, b) => b[1] - a[1]).map(([kind, hours]) => (
                <div key={kind}>
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-[12.5px] capitalize text-foreground">{kind}</span>
                    <span className="font-mono text-[11.5px] text-muted-foreground">{hours}h</span>
                  </div>
                  <Bar value={hours} max={data.hours.total} testId={`hours-bar-${kind}`} />
                </div>
              ))}
              <p className="pt-1 text-[11.5px] text-muted-foreground">
                {data.hours.entries} timesheet entries in this window.
              </p>
            </div>
          )}
        </Panel>

        <Panel title="Money" testId="panel-impact-money"
               kicker="Donations, ledger movements, payroll and the kitchen">
          <div className="space-y-1.5" data-testid="impact-money-rows">
            {[["Income", data.money.income], ["Of which donations", data.money.donations],
              ["Spend", data.money.spend], ["Payroll cost", data.money.payroll_cost],
              ["Unpaid leave deducted", data.money.unpaid_leave_deduction],
              ["Aid distributed", data.money.aid_distributed],
              ["Cafeteria takings", data.money.cafeteria_takings],
              ["Net", data.money.net]].map(([label, value]) => (
              <div key={label}
                   className="flex items-center justify-between border-b border-border/60 pb-1.5">
                <span className="text-[12.5px] text-muted-foreground">{label}</span>
                <span className={`font-mono text-[12px] ${label === "Net"
                  ? (value >= 0 ? "text-emerald-600 dark:text-emerald-400"
                    : "text-red-600 dark:text-red-400") : "text-foreground"}`}>
                  {money(value, "INR")}
                </span>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <Panel title="By programme" testId="panel-impact-programmes"
             kicker="Hours, budget and apprentices side by side">
        <Table testId="impact-programmes-table" head={<><Th>Programme</Th><Th>Hours</Th>
          <Th>Budget</Th><Th>Spent</Th><Th>Apprentices</Th><Th /></>}>
          {data.programmes.length === 0 && <Empty label="No programmes yet" colSpan={6} />}
          {data.programmes.map((p) => (
            <tr key={p.code} className="border-b border-border/60"
                data-testid={`impact-programme-${p.code}`}>
              <Td>{p.name}</Td>
              <Td className="font-mono text-[11px]">{p.hours}</Td>
              <Td className="font-mono text-[11px]">{money(p.budget, "INR", true)}</Td>
              <Td className="font-mono text-[11px]">{money(p.spent, "INR", true)}</Td>
              <Td className="font-mono text-[11px]">{p.apprentices}</Td>
              <Td className="w-[160px]"><Bar value={p.hours} max={maxHours} /></Td>
            </tr>
          ))}
        </Table>
      </Panel>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="People" testId="panel-impact-people" kicker="Everyone on the books">
          <div className="grid grid-cols-2 gap-3">
            {[["Staff", data.people.staff, HeartHandshake],
              ["Volunteers", data.people.volunteers, Users],
              ["With their own login", data.people.volunteers_with_logins, Users],
              ["Apprentices", data.people.apprentices, Building2],
              ["On placement", data.people.apprentices_on_placement, Building2],
              ["Beneficiaries", data.people.beneficiaries, HeartHandshake],
              ["Certificates issued", data.people.certificates, ShieldCheck]].map(
              ([label, value]) => (
                <div key={label} className="rounded-xl border border-border p-3">
                  <div className="text-[10.5px] uppercase tracking-[1.2px] text-muted-foreground">
                    {label}
                  </div>
                  <div className="font-display text-xl font-bold text-foreground">{value}</div>
                </div>
              ))}
          </div>
        </Panel>

        <Panel title="What needs attention" testId="panel-impact-attention"
               kicker="Across compliance, leave, reviews and the kitchen">
          <div className="space-y-2" data-testid="impact-attention-rows">
            {[["Evidence gaps against tracked rules", data.compliance.evidence_gaps],
              ["Leave requests awaiting a decision", data.compliance.leave_awaiting],
              ["Apprentice reviews overdue", data.compliance.reviews_overdue],
              ["Food safety sheets filed", data.compliance.food_safety_sheets],
              ["Food safety breaches recorded", data.compliance.food_safety_breaches],
              ["Hours awaiting approval", data.hours.awaiting_approval]].map(([label, value]) => (
              <div key={label}
                   className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
                <span className="text-[12.5px] text-foreground">{label}</span>
                <span className={`font-mono text-[12px] ${value
                  ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"}`}>
                  {value}
                </span>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
